"use client";

import * as React from "react";
import { FileCheck2, FileText, Upload, Eye, RefreshCw } from "lucide-react";
import { PAN_DOCUMENT_STATUS, type PanDocumentStatus } from "@artha/types";
import { Button } from "./button";
import { cn } from "../../lib/utils";

export interface UploadedDocumentMetadata {
  fileName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  status?: PanDocumentStatus;
}

export interface DocumentUploadStatusLabels {
  pendingReview: string;
  verified: string;
  rejected: string;
}

export interface DocumentUploadFieldProps {
  id?: string;
  title: string;
  description?: string;
  document?: UploadedDocumentMetadata | null;
  accept?: string;
  maxSizeMb?: number;
  uploadLabel?: string;
  replaceLabel?: string;
  viewLabel?: string;
  uploadingLabel?: string;
  viewingLabel?: string;
  fileTooLargeLabel?: string;
  statusLabels?: DocumentUploadStatusLabels;
  supportedText?: string;
  isUploading?: boolean;
  isViewing?: boolean;
  error?: string;
  onUpload: (file: File) => void;
  onView?: () => void;
  className?: string;
}

const DEFAULT_DOCUMENT_UPLOAD_LABELS = {
  upload: "Upload document",
  replace: "Replace document",
  view: "View",
  uploading: "Uploading...",
  viewing: "Opening...",
} as const;

const DEFAULT_DOCUMENT_UPLOAD_STATUS_LABELS: DocumentUploadStatusLabels = {
  pendingReview: "Pending review",
  verified: "Verified",
  rejected: "Needs update",
};

export function DocumentUploadField({
  id = "document-upload",
  title,
  description,
  document,
  accept = "image/*,application/pdf",
  maxSizeMb = 10,
  uploadLabel = DEFAULT_DOCUMENT_UPLOAD_LABELS.upload,
  replaceLabel = DEFAULT_DOCUMENT_UPLOAD_LABELS.replace,
  viewLabel = DEFAULT_DOCUMENT_UPLOAD_LABELS.view,
  uploadingLabel = DEFAULT_DOCUMENT_UPLOAD_LABELS.uploading,
  viewingLabel = DEFAULT_DOCUMENT_UPLOAD_LABELS.viewing,
  fileTooLargeLabel,
  statusLabels,
  supportedText,
  isUploading = false,
  isViewing = false,
  error,
  onUpload,
  onView,
  className,
}: DocumentUploadFieldProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [clientError, setClientError] = React.useState<string | null>(null);

  const formattedSize = document ? formatBytes(document.size) : null;
  const statusLabel = document?.status ? formatStatus(document.status, statusLabels) : null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const maxBytes = maxSizeMb * 1024 * 1024;
    if (file.size > maxBytes) {
      setClientError(fileTooLargeLabel || `File must be ${maxSizeMb}MB or smaller.`);
      return;
    }

    setClientError(null);
    onUpload(file);
  };

  return (
    <div className={cn("space-y-3 rounded-lg border border-black bg-white p-3", className)}>
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[#F2F4F7] text-[#344054]">
          {document ? <FileCheck2 className="size-5" aria-hidden="true" /> : <FileText className="size-5" aria-hidden="true" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#344054]">{title}</p>
          {description ? <p className="mt-0.5 text-xs leading-5 text-[#667085]">{description}</p> : null}
        </div>
      </div>

      {document ? (
        <div className="rounded-md bg-[#F9FAFB] px-3 py-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[#344054]">{document.fileName}</p>
              <p className="text-xs text-[#667085]">
                {[formattedSize, statusLabel].filter(Boolean).join(" / ")}
              </p>
            </div>
            {onView ? (
              <Button
                type="button"
                variant="outline"
                className="h-8 shrink-0 gap-1 rounded-md px-2 text-xs"
                onClick={onView}
                disabled={isViewing}
              >
                <Eye className="size-3.5" aria-hidden="true" />
                {isViewing ? viewingLabel : viewLabel}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <input
        ref={inputRef}
        id={id}
        type="file"
        className="sr-only"
        accept={accept}
        onChange={handleFileChange}
      />
      <Button
        type="button"
        className="h-10 w-full gap-2 rounded-lg"
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
      >
        {document ? <RefreshCw className="size-4" aria-hidden="true" /> : <Upload className="size-4" aria-hidden="true" />}
        {isUploading ? uploadingLabel : document ? replaceLabel : uploadLabel}
      </Button>

      <p className="text-xs leading-5 text-[#667085]">
        {supportedText || `Images or PDF up to ${maxSizeMb}MB.`}
      </p>
      {(clientError || error) ? <p className="text-xs text-red-500">{clientError || error}</p> : null}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(value >= 10 || index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatStatus(
  status: UploadedDocumentMetadata["status"],
  labels?: DocumentUploadStatusLabels
): string {
  const resolvedLabels = labels || DEFAULT_DOCUMENT_UPLOAD_STATUS_LABELS;
  if (status === PAN_DOCUMENT_STATUS.VERIFIED) return resolvedLabels.verified;
  if (status === PAN_DOCUMENT_STATUS.REJECTED) return resolvedLabels.rejected;
  return resolvedLabels.pendingReview;
}
